import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AIVisibility from "@/pages/AIVisibility";
import Generators from "@/pages/Generators";
import { Bot, Wand2 } from "lucide-react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSiteAudit } from "@/components/SiteAuditCard";
import { Button } from "@/components/ui/button";
import { scoreColor } from "@/lib/helpers";
import { SITE_STATUS_META, SITE_CATEGORIES } from "@/lib/siteAudit";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, Loader2, AlertCircle, ChevronDown, ChevronUp, Search, MapPin, Wrench, MousePointerClick,
  ListPlus, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, ExternalLink, Gauge, Sparkles,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const ICONS = { seo: Search, seo_local: MapPin, technique: Wrench, conversion: MousePointerClick };
const STATUSES = ["CRITIQUE", "IMPORTANT", "AMELIORER", "REUSSI", "NON_VERIFIABLE", "NON_APPLICABLE"];

function Finding({ f, auditId, onTask, onRechecked }) {
  const [open, setOpen] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const navigate = useNavigate();
  const m = SITE_STATUS_META[f.status];
  const isProblem = ["CRITIQUE", "IMPORTANT", "AMELIORER"].includes(f.status);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`finding-${f.check_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors" data-testid={`finding-toggle-${f.check_id}`}>
        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${m.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800 text-sm">{f.label}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>{m.label}</span>
            <span className="text-[11px] text-slate-400">{f.category_name} · {f.sub}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1 truncate">{f.observed}</p>
        </div>
        <span className={`font-mono text-sm font-semibold ${scoreColor(f.score)} shrink-0`}>{f.score == null ? "—" : `${f.score}`}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100" data-testid={`finding-detail-${f.check_id}`}>
          <Block title="Ce qui a été détecté" text={f.observed} />
          <Block title="Pourquoi c'est important" text={f.explanation} />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Où se trouve le problème</div>
            {f.pages?.length ? (
              <ul className="space-y-0.5">{f.pages.map((u) => <li key={u} className="text-xs font-mono text-blue-700 truncate"><a href={u} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">{u}<ExternalLink className="w-3 h-3" /></a></li>)}</ul>
            ) : <p className="text-sm text-slate-500">{isProblem ? "Ensemble du site" : "Aucune page concernée"}</p>}
          </div>
          {isProblem && (
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-500 mb-1">Comment le corriger</div>
              <p className="text-sm text-blue-900">{f.recommendation}</p>
              <div className="text-xs text-blue-700/70 mt-1">Difficulté : {f.difficulty} · Temps estimé : {f.time} · Poids : {f.weight}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" onClick={() => onTask(f.check_id)} data-testid={`finding-task-${f.check_id}`} className="h-8 rounded-lg bg-blue-600 hover:bg-blue-700">
                  <ListPlus className="w-4 h-4 mr-1.5" /> Ajouter au plan d'action
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/app/advisor?q=${encodeURIComponent(`Explique-moi simplement le problème « ${f.label} » (contrôle ${f.check_id}) détecté sur mon site et génère-moi une correction.`)}`)} data-testid={`finding-ask-ai-${f.check_id}`} className="h-8 rounded-lg border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Demander au Conseiller
                </Button>
                <Button size="sm" variant="outline" disabled={rechecking} onClick={async () => {
                  setRechecking(true);
                  try {
                    const { data } = await api.post(`/site-audits/${auditId}/recheck/${f.check_id}`);
                    onRechecked(data);
                    toast[data.status === "REUSSI" ? "success" : "info"](data.status === "REUSSI" ? "Correction confirmée : contrôle réussi" : `Re-vérifié : ${SITE_STATUS_META[data.status].label} — ${data.observed}`);
                  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                  finally { setRechecking(false); }
                }} data-testid={`finding-recheck-${f.check_id}`} className="h-8 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  {rechecking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} J'ai corrigé, re-vérifier
                </Button>
              </div>
              {f.rechecked_at && <p className="text-[11px] text-emerald-700 mt-2" data-testid={`finding-rechecked-${f.check_id}`}>Re-vérifié le {new Date(f.rechecked_at).toLocaleString("fr-FR")} — {f.scope_note}</p>}
            </div>
          )}
          {f.rechecked_at && f.status === "REUSSI" && <p className="text-[11px] text-emerald-700" data-testid={`finding-rechecked-${f.check_id}`}>Correction confirmée le {new Date(f.rechecked_at).toLocaleString("fr-FR")} — {f.scope_note}</p>}
          <p className="text-[11px] text-slate-400">Vérifié le {f.checked_at ? new Date(f.checked_at).toLocaleString("fr-FR") : "—"}</p>
        </div>
      )}
    </div>
  );
}

function Block({ title, text }) {
  return <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{title}</div><p className="text-sm text-slate-700">{text}</p></div>;
}

function Diff({ diff }) {
  if (!diff) return null;
  const Row = ({ items, label, Icon, cls }) => items.length > 0 && (
    <div>
      <div className={`text-xs font-semibold flex items-center gap-1 ${cls}`}><Icon className="w-3.5 h-3.5" /> {label} ({items.length})</div>
      <ul className="mt-1 space-y-0.5">{items.map((i) => <li key={i.check_id} className="text-xs text-slate-600">{i.label} <span className="text-slate-400">({i.before} → {i.after}{i.score_before != null ? `, ${i.score_before}→${i.score_after}` : ""})</span></li>)}</ul>
    </div>
  );
  const empty = !diff.appeared.length && !diff.fixed.length && !diff.improved.length && !diff.regressed.length;
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="site-audit-diff">
      <h2 className="font-heading font-semibold mb-1">Depuis la dernière analyse</h2>
      <p className="text-xs text-slate-400 mb-3">Comparé au {new Date(diff.previous_at).toLocaleString("fr-FR")} · score global {diff.scores.global.before ?? "—"} → {diff.scores.global.after ?? "—"}</p>
      {empty ? <p className="text-sm text-slate-500">Aucun changement détecté.</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Row items={diff.fixed} label="Problèmes corrigés" Icon={CheckCircle2} cls="text-emerald-600" />
          <Row items={diff.appeared} label="Nouveaux problèmes" Icon={AlertTriangle} cls="text-red-600" />
          <Row items={diff.improved} label="Améliorations" Icon={TrendingUp} cls="text-blue-600" />
          <Row items={diff.regressed} label="Régressions" Icon={TrendingDown} cls="text-orange-600" />
        </div>
      )}
    </div>
  );
}

export default function SiteAudit() {
  const { current } = useEstablishments();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = ["ai", "generators"].includes(sp.get("tab")) ? sp.get("tab") : "site";
  if (!current) return <div className="p-8 text-slate-500">Aucun établissement sélectionné.</div>;
  return <div><div className="px-4 sm:px-6 lg:px-8 pt-4 max-w-7xl mx-auto"><div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 w-fit" data-testid="site-audit-tabs">{[["site", "Audit technique du site"], ["ai", "Visibilité IA"], ["generators", "Générateurs SEO"]].map(([k, l]) => <button key={k} onClick={() => setSp(k === "site" ? {} : { tab: k })} className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 ${tab === k ? "bg-white shadow-sm font-medium" : "text-slate-600"}`} data-testid={`site-audit-tab-${k}`}>{k === "ai" && <Bot className="w-3.5 h-3.5" />}{k === "generators" && <Wand2 className="w-3.5 h-3.5" />}{l}</button>)}</div></div>{tab === "ai" ? <AIVisibility embedded /> : tab === "generators" ? <Generators embedded /> : <SiteAuditInner key={current.id} est={current} navigate={navigate} />}</div>;
}

function SiteAuditInner({ est, navigate }) {
  const { data, start, load } = useSiteAudit(est.id);
  const [filter, setFilter] = useState("ALL");
  const [cat, setCat] = useState("ALL");
  const latest = data?.latest;
  const done = latest?.status === "done" ? latest : null;
  const running = latest?.status === "running";

  const addTask = async (checkId) => {
    try {
      await api.post(`/site-audits/${done.id}/tasks/${checkId}`);
      toast.success("Tâche ajoutée au plan d'action");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 grid place-items-center min-h-[60vh]"><Gauge className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  const findings = (done?.findings || []).filter((f) => (filter === "ALL" || f.status === filter) && (cat === "ALL" || f.category === cat));
  const history = (data.history || []).map((h) => ({ name: new Date(h.finished_at).toLocaleDateString("fr-FR"), Global: h.global_score, ...Object.fromEntries((h.categories || []).map((c) => [c.name, c.score])) }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <button onClick={() => navigate("/app/dashboard")} data-testid="site-audit-back-button" className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Retour au tableau de bord</button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Globe className="w-7 h-7 text-blue-600" /> Audit du site</h1>
          <p className="text-slate-500 text-sm mt-1">{est.website ? <a href={est.website} target="_blank" rel="noreferrer" className="hover:underline">{est.website}</a> : "Aucune URL renseignée"} · Données mesurées réellement par Respondia, jamais inventées.</p>
        </div>
        <Button onClick={start} disabled={running || !data.can_run} data-testid="site-audit-run-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}{running ? "Analyse en cours…" : "Analyser mon site"}
        </Button>
      </div>

      {data.is_demo && <div className="mt-5 rounded-xl bg-amber-50/60 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="site-audit-demo-notice">DONNÉES DE DÉMONSTRATION — l'analyse réelle du site n'est pas disponible sur l'établissement démo.</div>}
      {running && <div className="mt-5 text-sm text-blue-700 bg-blue-50 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="site-audit-page-running"><Loader2 className="w-4 h-4 animate-spin" /> Exploration en cours… mise à jour automatique.</div>}
      {latest?.status === "error" && <div className="mt-5 text-sm text-red-700 bg-red-50 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="site-audit-page-error"><AlertCircle className="w-4 h-4" /> {latest.error}</div>}
      {!latest && data.can_run && <div className="mt-8 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="site-audit-page-empty">Aucune analyse. Cliquez sur « Analyser mon site ».</div>}

      {done && (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="site-audit-page-global">
              <div className="text-[11px] uppercase tracking-wide text-white/60">Score du site</div>
              <div className="font-heading text-4xl font-extrabold">{done.global_score ?? "—"}</div>
              <div className="text-xs text-white/60 mt-1">{done.pages_count} pages · {done.crawl_meta?.duration_s}s</div>
            </div>
            {SITE_CATEGORIES.map((c) => {
              const x = done.categories.find((k) => k.key === c.key); const Icon = ICONS[c.key];
              return (
                <button key={c.key} onClick={() => setCat(cat === c.key ? "ALL" : c.key)} data-testid={`site-audit-cat-${c.key}`}
                  className={`text-left p-4 rounded-2xl border transition-all ${cat === c.key ? "border-blue-400 bg-blue-50" : "bg-white border-slate-200 hover:border-blue-300"}`}>
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400"><Icon className="w-3.5 h-3.5" /> {c.label}</div>
                  <div className={`font-heading text-4xl font-extrabold ${scoreColor(x?.score)}`}>{x?.score ?? "—"}</div>
                  <div className="text-xs text-slate-400 mt-1">{x?.counts.CRITIQUE + x?.counts.IMPORTANT} à corriger · {x?.counts.REUSSI} réussis</div>
                </button>
              );
            })}
          </div>

          {done.diff && <div className="mt-6"><Diff diff={done.diff} /></div>}

          {history.length > 1 && (
            <div className="mt-6 bg-white rounded-2xl p-5 border border-slate-200" data-testid="site-audit-history">
              <h2 className="font-heading font-semibold mb-3">Évolution des scores</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={history} margin={{ left: -20, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 12 }} /><Tooltip /><Legend />
                  <Line type="monotone" dataKey="Global" stroke="#0f172a" strokeWidth={3} />
                  {["SEO on-page", "SEO local du site", "Technique", "Conversion"].map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={["#2563eb", "#10b981", "#f59e0b", "#a855f7"][i]} strokeWidth={2} dot={false} />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-lg font-semibold mr-2">Contrôles ({findings.length})</h2>
            {["ALL", ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`site-audit-filter-${s}`}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${filter === s ? "bg-slate-900 text-white" : `${SITE_STATUS_META[s]?.bg || "bg-slate-100"} ${SITE_STATUS_META[s]?.text || "text-slate-600"}`}`}>
                {s === "ALL" ? "Tous" : `${SITE_STATUS_META[s].label} (${done.counts[s] ?? 0})`}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            {findings.map((f) => <Finding key={f.check_id} f={f} auditId={done.id} onTask={addTask} onRechecked={load} />)}
            {findings.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Aucun contrôle pour ce filtre.</p>}
          </div>

          <h2 className="font-heading text-lg font-semibold mt-10 mb-3">Pages analysées ({done.pages.length})</h2>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="site-audit-pages">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400"><tr><th className="text-left p-3">URL</th><th className="p-3">HTTP</th><th className="text-left p-3">Title</th><th className="p-3">Meta</th><th className="p-3">H1</th><th className="p-3">Mots</th><th className="p-3">Index</th></tr></thead>
              <tbody>
                {done.pages.map((p) => (
                  <tr key={p.url} className="border-t border-slate-100" data-testid={`site-audit-page-row`}>
                    <td className="p-3 font-mono text-xs text-blue-700 max-w-xs truncate"><a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.url}</a></td>
                    <td className={`p-3 text-center font-mono ${p.status && p.status < 400 ? "text-emerald-600" : "text-red-500"}`}>{p.status ?? p.error}</td>
                    <td className="p-3 max-w-xs truncate">{p.title || <span className="text-red-500">absent</span>}</td>
                    <td className="p-3 text-center">{p.meta_len ? `${p.meta_len} c.` : <span className="text-red-500">—</span>}</td>
                    <td className="p-3 text-center">{p.h1_count ?? "—"}</td>
                    <td className="p-3 text-center">{p.word_count ?? "—"}</td>
                    <td className="p-3 text-center">{p.noindex ? <span className="text-red-500">noindex</span> : p.status && p.status < 400 ? "oui" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
